import { Helmet } from 'react-helmet-async';
import { Box } from '@mui/material';
import {
  Container,
  Button,
  Typography,
  Body,
  CircularIconButton,
  GradientBackground,
} from '@planitgeo/treeplotter-ui-library';
import { useLocales } from 'locales';
import { sentenceCase } from 'change-case';
import { externalLogout } from 'identityAndAccess/infrastructure/utils';
import { useLogoutState } from 'identityAndAccess/infrastructure/useLogoutState';
import loggerService, { LoggerLevel } from 'common/infrastructure/log';
import { clearReactQueryCache } from 'common/infrastructure/remoteState/persistQueryClient';
import { sharedStyles } from 'styles/sharedStyles';
import QuestionMark from '@mui/icons-material/QuestionMark';
import Logout from '@mui/icons-material/Logout';

const onError = (error: object) => {
  loggerService.log(LoggerLevel.ERROR, 'Error Caught by react-boundary!', error);
};

const SUPPORT_EMAIL = 'mailto:support@planitgeo.com';

const ErrorPage = ({ error }: { error: unknown }) => {
  const { translate } = useLocales();
  const logoutInProgress = useLogoutState();

  return (
    <>
      <Helmet>
        <title>{sentenceCase(translate('error'))}</title>
      </Helmet>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100dvh',
        }}
      >
        <Box sx={{ bgcolor: '#FFFFFF' }}>
          <Box sx={{ mt: '100px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box
              component="img"
              src="/assets/error%20art.svg"
              alt=""
              sx={{
                width: '100%',
                maxWidth: 320,
                maxHeight: '33vh',
                objectFit: 'contain',
                mb: 0,
              }}
              data-testid="error_icon"
            />
          </Box>
        </Box>
        <GradientBackground
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Container
            sx={[
              sharedStyles['responsive-container-no-padding'],
              { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 },
            ]}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flex: 1,
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  py: 4,
                  px: 2,
                }}
              >
                <Typography
                  variant="h1"
                  sx={{ textAlign: 'center', px: 1, mb: 6, whiteSpace: 'pre-line' }}
                  data-testid="error_heading"
                >
                  {translate('error_pun_message')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 700, textAlign: 'center', mb: 3, whiteSpace: 'pre-line' }}
                  data-testid="error_message"
                >
                  {translate('error_reset_instruction')}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  shape="pill"
                  onClick={async () => {
                    const idToken = localStorage.getItem('idToken');
                    localStorage.clear();
                    localStorage.setItem('idToken', idToken || '');
                    await clearReactQueryCache();
                    window.location.reload();
                  }}
                  data-testid="error_reset_button"
                  sx={{ mb: 3 }}
                >
                  {translate('reset')}
                </Button>
                <Body variant="body1" sx={{ textAlign: 'center', mb: 1 }}>
                  {translate('error_contact_support_if_needed')}
                </Body>
                <Box
                  component="a"
                  href={SUPPORT_EMAIL}
                  aria-label={String(translate('contact_support'))}
                  sx={{ display: 'inline-flex', textDecoration: 'none', mb: 4 }}
                >
                  <CircularIconButton
                    aria-label={String(translate('contact_support'))}
                    data-testid="error_contact_support_button"
                  >
                    <QuestionMark />
                  </CircularIconButton>
                </Box>
              </Box>
              <Box sx={{ flex: 1, minHeight: 24 }} />
              <Button
                variant="text"
                color="inherit"
                startIcon={<Logout />}
                disabled={logoutInProgress}
                onClick={() => externalLogout({ skipLogoutWhenOffline: false })}
                data-testid="error_logout_button"
                sx={{ color: 'black' }}
              >
                {translate('auth.logout')}
              </Button>
            </Box>
          </Container>
        </GradientBackground>
      </Box>
    </>
  );
};

export { ErrorPage, onError };
